//OutstaffingClientsForms  Формы клиента аутов
import React, { useState, useEffect } from "react";
import { Col, Row, Button, Input, Form, Checkbox } from 'antd';

// Zustand store
import { useShallow } from 'zustand/react/shallow';
import { useCustomersOutstaff, useCompaniesStore, useContractsStore, useProcuratory } from 'storeZustand';

import { updateCustomerOutstaff } from 'services/customersOutstaff';
import { patchContract } from 'services';

import SelectProcuratory from './selectProcuratory';
import SelectCompany from './selectCompany';
import { useClientFields } from 'hooks';

import StarIcon from 'components/Common/Icons/StarIcon';
import SelectExternalCompany from './selectExternalCompany';

const OutstaffingClientsForms = ({ isEdit, selectedCity }) => {
	const [isCheckedFiz, setIsCheckedFiz] = useState(true); // активен ли чекбокс физик
	const [form] = Form.useForm();

	// Договоры клиента
	const [
		contractById,
		contractId, // id договора
		getTodayContracts
	] = useContractsStore(useShallow((state) => [
		state.contractById,
		state.contractId,
		state.getTodayContracts
	]));

	// Все клиенты аутстафф
	const [
		customerOutstaffId, // id клиента
		setCustomerOutstaffId,
		addCustomerOutstaff // добавить клиента
	] = useCustomersOutstaff((state) => [
		state.customerOutstaffId,
		state.setCustomerOutstaffId,
		state.addCustomerOutstaff
	]);

	// Все доверенности
	const [
		selectedProcuratory,
	] = useProcuratory(useShallow((state) => [
		state.selectedProcuratory,
	]));

	// Все компании
	const [
		selectedCompany = null,
		selectedExternalCompany, // Выбранная внешняя компания (оптовик)
		setSelectedExternalCompany // установить компанию оптовика
	] = useCompaniesStore(useShallow((state) => [
		state.selectedCompany,
		state.selectedExternalCompany,
		state.setSelectedExternalCompany
	]));

	const fields = useClientFields(contractById?.client);

	// Выбранный оптовик при редактировании
	useEffect(() => {
		if (contractById?.customer_company) {
			setSelectedExternalCompany(contractById.customer_company);
			setIsCheckedFiz(false); // снимаем физика, если есть оптовик
		}
	}, [contractById]);

	// Обновление или добавление клиента
	const onFinish = async (values) => {
		const id = isEdit ? customerOutstaffId : undefined;

		const updatedClient = {
			first_name: values.first_name,
			first_name_dat_padeg: values.first_name_dat_padeg,
			second_name: values.second_name || null,
			second_name_dat_padeg: values.second_name_dat_padeg || null,
			last_name: values.last_name,
			last_name_dat_padeg: values.last_name_dat_padeg,
			fio: values.fio,
			fio_rod_padeg: values.fio_rod_padeg,
			dob: values.dob,
			phone: values.phone || null,
			passport_place_created: values.passportPlaceCreated || null,
			passport_address: values.passportAddress,
			mail_address: values.mail_address, // почтовый адрес
			profession: values.profession,
			skill: values.skill,
			bank_account: values.bank_account,
			company: selectedCompany, // id компании
			birth_place: values.birth_place || null,
			citizen: values.citizen || null,
			description: values.description,
		};

		// если редактирование и есть id
		if (isEdit && id) {
			await updateCustomerOutstaff({ updatedClient, id });

			// Обновление договора
			const updatedContract = {
				procuratory: selectedProcuratory || null, // Доверенность
				customer_company: selectedExternalCompany || null, // Компания оптовик
			}

			await patchContract({ id: contractId, updatedContract });
		} else {
			let customerId = await addCustomerOutstaff(updatedClient); // добавление клиента
			setCustomerOutstaffId(customerId);

			// Обновление договора
			const updatedContract = {
				client: customerId,
				procuratory: selectedProcuratory || null, // Доверенность
				customer_company: selectedExternalCompany || null, // Компания оптовик
			}

			await patchContract({ id: contractId, updatedContract });
			customerId = null;
		}

		await getTodayContracts(selectedCity);
	};

	const onChangeFiz = () => {
		setIsCheckedFiz(!isCheckedFiz);
	};

	return (
		<>
			<Row className="mb-3 align-items-center" gutter={16}>
				<Col span={3}>
					<Checkbox checked={isCheckedFiz} onChange={onChangeFiz}>Физик</Checkbox>
				</Col>
				<Col span={8}>
					<SelectExternalCompany isCheckedFiz={isCheckedFiz} />
				</Col>
			</Row>
			<Row className="mb-3" gutter={16}>
				<Col span={10}>
					{/* Выбор компании */}
					<SelectCompany company={contractById?.client?.company?.id} />
				</Col>
				<Col span={14}>
					{/* Выбор доверенности */}
					<SelectProcuratory />
				</Col>
			</Row>
			<Form
				fields={fields}
				onFinish={onFinish}
				autoComplete="off"
				form={form}
			>
				{/* Фамилия */}
				<Row gutter={16}>
					<Col span={12}>
						<label className="form-label archi-label me-1">Фамилия</label>
						<StarIcon />
						<Form.Item
							name="last_name"
							rules={[
								{
									required: true,
									message: 'Введите фамилию!',
								},
							]}
						>
							<Input autoComplete='on' />
						</Form.Item>
					</Col>
					<Col span={12}>
						<label className="form-label archi-label me-1">Фамилия в дат. падеже (Кому?)</label>
						<StarIcon />
						<Form.Item
							name="last_name_dat_padeg"
							rules={[
								{
									required: true,
									message: 'Введите фамилию в дат. падеже!',
								},
							]}
						>
							<Input autoComplete='on' />
						</Form.Item>
					</Col>
				</Row>
				{/* Имя */}
				<Row gutter={16}>
					<Col span={12}>
						<label className="form-label archi-label me-1">Имя</label>
						<StarIcon />
						<Form.Item
							name="first_name"
							rules={[
								{
									required: true,
									message: 'Введите имя!',
								},
							]}
						>
							<Input autoComplete='on' />
						</Form.Item>
					</Col>
					<Col span={12}>
						<label className="form-label archi-label me-1">Имя в дат. падеже (Кому?)</label>
						<StarIcon />
						<Form.Item
							name="first_name_dat_padeg"
							rules={[
								{
									required: true,
									message: 'Введите имя в дат. падеже!',
								},
							]}
						>
							<Input autoComplete='on' />
						</Form.Item>
					</Col>
				</Row>

				{/* Отчество */}
				<Row gutter={16}>
					<Col span={12}>
						<label className="form-label archi-label">Отчество</label>
						<Form.Item
							name="second_name"

						>
							<Input autoComplete='on' />
						</Form.Item>
					</Col>
					<Col span={12}>
						<label className="form-label archi-label">Отч. в дат. падеже (Кому?)</label>
						<Form.Item
							name="second_name_dat_padeg"

						>
							<Input autoComplete='on' />
						</Form.Item>
					</Col>
				</Row>
				{/* ФИО в род. падеже */}
				<Row gutter={16}>
					<Col span={12}>
						<label className="form-label archi-label me-1">ФИО в род. падеже (от кого?)</label>
						<StarIcon />
						<Form.Item
							name="fio_rod_padeg"
							rules={[
								{
									required: true,
									message: 'Введите ФИО в род. падеже!',
								},
							]}
						>
							<Input autoComplete='on' />
						</Form.Item>
					</Col>
					<Col span={12}>
						<div>
							<label className="form-label archi-label me-1">ФИО с инициалами</label>
							<StarIcon />
							<Form.Item
								name="fio"
								rules={[
									{
										required: true,
										message: 'Введите ФИО с инициалами!',
									},
								]}
							>
								<Input autoComplete='on' />
							</Form.Item>
						</div>
					</Col>
				</Row>
				{/* Место рождения, дата рождения */}
				<Row gutter={16}>
					<Col span={12}>
						<label className="form-label archi-label me-1">Место рождения</label>
						<Form.Item
							name="birth_place"

						>
							<Input autoComplete='on' />
						</Form.Item>
					</Col>
					<Col span={12}>
						<label className="form-label me-1">Дата рождения</label>
						<StarIcon />
						<Form.Item
							name="dob"
							rules={[
								{
									required: true,
									message: 'Введите дату рождения!',
								},
							]}
						>
							<Input type='date' min="1900-01-01" max="2100-01-01" />
						</Form.Item>
					</Col>
				</Row>
				{/* Профессия */}
				<Row gutter={16}>
					<Col span={12}>
						<label className="form-label archi-label me-1">Профессия</label>
						<StarIcon />
						<Form.Item
							name="profession"
							rules={[
								{
									required: true,
									message: 'Введите профессию!',
								},
							]}
						>
							<Input autoComplete='on' />
						</Form.Item>
					</Col>
					<Col span={12}>
						<label className="form-label archi-label me-1">Проф. в род. пад. (Кого?)</label>
						<StarIcon />
						<Form.Item
							name="skill"
							rules={[
								{
									required: true,
									message: 'Введите профессию в род. пад!',
								},
							]}
						>
							<Input autoComplete='on' />
						</Form.Item>
					</Col>
				</Row>

				{/* телефон, расч. счет */}
				<Row gutter={16}>
					<Col span={12}>
						<label className="form-label archi-label">Телефон</label>
						<Form.Item
							name="phone"

						>
							<Input autoComplete='on' />
						</Form.Item>
					</Col>

					<Col span={12}>
						<label className="form-label archi-label">Расч. счет</label>
						<Form.Item
							name="bank_account"

						>
							<Input autoComplete='on' />
						</Form.Item>
					</Col>
				</Row>

				{/* <Row>
					<Col span={24}>
						<label className="form-label archi-label">Почтовый адрес</label>
						<Form.Item
							name="mail_address"

						>
							<Input autoComplete='on' />
						</Form.Item>
					</Col>
				</Row> */}
				<Row>
					<Col span={24}>
						<label className="form-label archi-label">Комментарий</label>
						<Form.Item
							name="description"

						>
							<Input autoComplete='on' />
						</Form.Item>
					</Col>
				</Row>

				<div className="text-end">
					<Form.Item noStyle={true}>
						<Button type="primary" htmlType="submit">
							{isEdit ? 'Обновить клиента' : 'Сохранить клиента'}
						</Button>
					</Form.Item>
				</div>
			</Form>
		</>
	)
}

export default OutstaffingClientsForms;